import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from '../../api/client'
import { Lightbox, VisorDeDocumento } from '../VisorDocumento'
import type { Attachment, AttachmentKind } from '../../types/portal'
import { pesoLegible, rutaDeVista, seCargaSola } from './adjuntoReglas'

/**
 * El archivo que mandó el cliente, dentro de la burbuja del chat.
 *
 * Antes acá salía el texto crudo `[document] R.SERVICIOS.pdf`, que es
 * literalmente lo que `conversation/handler` guarda como contenido del
 * mensaje. Para saber qué había mandado el cliente el asesor tenía que leer la
 * extensión a ojo, y para verlo tenía que irse a la pestaña Documentos y
 * adivinar cuál de la lista era.
 *
 * Ahora se ve como en WhatsApp (05-oct-2026): la foto y el sticker, la primera
 * hoja del PDF con sus páginas y su peso, y el audio y el video con su
 * reproductor. Todo se abre en el visor del portal, no en otra pestaña
 * (28-set-2026), y se cierra con Esc.
 *
 * LA URL ES TEMPORAL. Las URL firmadas de S3 vencen a los 5 minutos. Se piden
 * solas cuando la burbuja aparece en pantalla —no al montar: el historial
 * abierto puede traer 40 y casi nadie las mira todas— y se recuerdan por ruta
 * mientras sirven (`guardados`, 4 min): al volver a la conversación, la foto ya
 * está. Si igual vence, falla al cargar y se pide otra, una vez. El audio y el
 * video se piden igual, pero el reproductor no baja nada hasta que se toca ▶.
 */

const ETIQUETA: Record<AttachmentKind, string> = {
  image: 'Imagen',
  pdf: 'PDF',
  audio: 'Audio',
  video: 'Video',
  doc: 'Documento',
  sheet: 'Hoja de cálculo',
  file: 'Archivo',
  sticker: 'Sticker',
}

/** Color del ícono por tipo, con los tokens del portal. */
const TONO: Record<AttachmentKind, string> = {
  image: 'text-purple',
  pdf: 'text-danger',
  audio: 'text-info',
  video: 'text-info',
  doc: 'text-info',
  sheet: 'text-brand',
  file: 'text-ink-muted',
  sticker: 'text-purple',
}

/** Lo que devuelven `/preview`, `/media` y `/miniatura`. */
interface Vista {
  url: string | null
  paginas?: number | null
  bytes?: number | null
}

/** Lo ya pedido, por ruta. S3 firma por 5 minutos; se reusa hasta los 4 para
 *  no servir una URL que vence mientras la imagen baja. */
const VIGENCIA_MS = 4 * 60 * 1000
const guardados = new Map<string, Vista & { vence: number }>()

function vigente(ruta: string): Vista | null {
  const guardado = guardados.get(ruta)
  if (!guardado) return null
  if (Date.now() >= guardado.vence) {
    guardados.delete(ruta)
    return null
  }
  return guardado
}

function IconoArchivo({ kind }: { kind: AttachmentKind }) {
  // Una hoja con la esquina doblada, común a todos, más una marca por tipo:
  // se reconoce la forma de "archivo" y el detalle dice cuál.
  const marca =
    kind === 'image' || kind === 'sticker' ? <circle cx="8" cy="13" r="1.6" fill="currentColor" />
    : kind === 'audio' ? <path d="M7 12v4M9.5 10.5v7M12 12.5v3" stroke="currentColor"
                              strokeWidth="1.4" strokeLinecap="round" />
    : kind === 'video' ? <path d="m7.5 11 4.5 2.8-4.5 2.8z" fill="currentColor" />
    : kind === 'sheet' ? <path d="M6.5 12h7M6.5 15h7M10 12v5" stroke="currentColor"
                              strokeWidth="1.2" />
    : <path d="M6.5 12.5h7M6.5 15.5h4.5" stroke="currentColor" strokeWidth="1.4"
            strokeLinecap="round" />

  return (
    <svg width="22" height="22" viewBox="0 0 20 22" fill="none" aria-hidden>
      <path d="M3.5 2.5h8l5 5v12a1 1 0 0 1-1 1h-12a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1z"
            stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M11.5 2.5v5h5" stroke="currentColor" strokeWidth="1.3"
            strokeLinejoin="round" />
      {marca}
    </svg>
  )
}

export default function Adjunto({ attachment: a }: { attachment: Attachment }) {
  const ruta = rutaDeVista(a)
  const autoCarga = seCargaSola(a.kind) && Boolean(ruta)
  // Si se vio hace un rato, la URL sigue sirviendo: se muestra de entrada.
  const [vista, setVista] = useState<Vista | null>(() => (ruta ? vigente(ruta) : null))
  const [cargando, setCargando] = useState(false)
  // Un fallo no se reintenta solo: lo reintenta el asesor con un clic.
  const [fallo, setFallo] = useState(false)
  // El visor pide su propia URL al abrirse: vence a los 5 min, así que
  // reusar una vieja mostraría el error de S3 en vez del documento.
  const [visor, setVisor] = useState(false)
  const [ampliada, setAmpliada] = useState(false)
  // Cuántas veces el archivo falló al mostrarse: una URL vencida se repide UNA vez.
  const fallosAlMostrar = useRef(0)
  const tarjeta = useRef<HTMLDivElement>(null)

  const cargar = useCallback(async () => {
    if (!ruta || cargando) return
    const ya = vigente(ruta)
    if (ya) {
      setVista(ya)
      return
    }
    setCargando(true)
    try {
      const res = await apiClient.get<Vista>(ruta)
      const nueva = { url: res.data.url ?? null, paginas: res.data.paginas, bytes: res.data.bytes }
      guardados.set(ruta, { ...nueva, vence: Date.now() + VIGENCIA_MS })
      setVista(nueva)
      setFallo(false)
    } catch {
      setFallo(true)
    } finally {
      setCargando(false)
    }
  }, [ruta, cargando])

  // Se pide sola cuando la burbuja se ve (o está por verse).
  useEffect(() => {
    if (!autoCarga || vista || fallo || cargando) return
    const nodo = tarjeta.current
    if (!nodo || typeof IntersectionObserver === 'undefined') {
      void cargar()
      return
    }
    const observador = new IntersectionObserver((entradas) => {
      if (entradas.some((e) => e.isIntersecting)) {
        observador.disconnect()
        void cargar()
      }
    }, { rootMargin: '200px' })
    observador.observe(nodo)
    return () => observador.disconnect()
  }, [autoCarga, vista, fallo, cargando, cargar])

  const falloAlMostrar = () => {
    // Lo más probable es una URL vencida: se olvida y se pide otra, una vez.
    guardados.delete(ruta)
    fallosAlMostrar.current += 1
    if (fallosAlMostrar.current > 1) setFallo(true)
    setVista(null)
  }

  const reintentar = () => {
    fallosAlMostrar.current = 0
    setFallo(false)
    void cargar()
  }

  const abrible = Boolean(a.doc_id || a.preview_path)
  const url = vista?.url ?? null

  // ── Foto: se muestra, y el clic la abre en grande ──────────────────────────
  if (a.kind === 'image' && url) {
    return (
      <div ref={tarjeta}>
        <button onClick={() => setAmpliada(true)} title="Ampliar"
                className="block cursor-zoom-in overflow-hidden rounded-[10px] border border-surface-faint">
          <img src={url} alt={a.filename} onError={falloAlMostrar}
               className="max-h-[220px] max-w-full object-cover" />
        </button>
        {ampliada && (
          <Lightbox preview={{ url, filename: a.filename, mime_type: 'image/*' }}
                    onClose={() => setAmpliada(false)} />
        )}
      </div>
    )
  }

  // ── Sticker: como en WhatsApp, sin marco ───────────────────────────────────
  if (a.kind === 'sticker' && url) {
    return (
      <div ref={tarjeta}>
        <img src={url} alt="Sticker" onError={falloAlMostrar}
             className="h-[120px] w-[120px] object-contain" />
      </div>
    )
  }

  // ── Audio y video: el reproductor no baja nada hasta que se toca ▶ ─────────
  if (a.kind === 'audio' && url) {
    return (
      <div ref={tarjeta} className="flex flex-col gap-1">
        <audio controls preload="none" src={url} onError={falloAlMostrar}
               className="w-[260px] max-w-full">
          Tu navegador no puede reproducir este audio.
        </audio>
        <span className="text-pill text-ink-muted">Audio del cliente</span>
      </div>
    )
  }
  if (a.kind === 'video' && url) {
    return (
      <div ref={tarjeta}>
        <video controls preload="metadata" src={url} onError={falloAlMostrar}
               className="max-h-[220px] max-w-full rounded-[10px] bg-black">
          Tu navegador no puede reproducir este video.
        </video>
      </div>
    )
  }

  // ── PDF: la primera hoja, y debajo nombre, páginas y peso ──────────────────
  if (a.kind === 'pdf') {
    const paginas = vista?.paginas ?? a.paginas ?? null
    const bytes = vista?.bytes ?? a.bytes ?? null
    const detalle = [
      paginas ? `${paginas} página${paginas === 1 ? '' : 's'}` : '',
      'PDF',
      bytes ? pesoLegible(bytes) : '',
    ].filter(Boolean).join(' · ')
    return (
      <div ref={tarjeta}>
        <button
          onClick={() => { if (a.doc_id) setVisor(true) }}
          disabled={!a.doc_id}
          title={a.doc_id ? a.filename : 'El archivo todavía se está procesando'}
          className={`block w-[260px] max-w-full overflow-hidden rounded-[10px] border border-surface-faint bg-white/70 text-left transition-colors ${
            a.doc_id ? 'hover:bg-surface' : 'cursor-default opacity-70'
          }`}
        >
          {url && (
            <img src={url} alt="" onError={falloAlMostrar}
                 className="h-[120px] w-full border-b border-surface-faint bg-white object-cover object-top" />
          )}
          <span className="flex items-center gap-2.5 px-2.5 py-2">
            <span className={`shrink-0 ${TONO.pdf}`}><IconoArchivo kind="pdf" /></span>
            <span className="min-w-0">
              <span className="block max-w-[200px] truncate text-chat font-semibold text-ink">
                {a.filename}
              </span>
              <span className="block text-pill text-ink-muted">
                {a.doc_id ? detalle : 'PDF · procesando'}
              </span>
            </span>
          </span>
        </button>
        {visor && a.doc_id && (
          <VisorDeDocumento documentId={a.doc_id} filename={a.filename}
                            onClose={() => setVisor(false)} />
        )}
      </div>
    )
  }

  // ── El resto, o mientras carga: la tarjeta ─────────────────────────────────
  const seMuestraAdentro = seCargaSola(a.kind)
  return (
    <div ref={tarjeta} className="flex flex-col gap-1">
      <button
        onClick={seMuestraAdentro ? reintentar : () => { if (a.doc_id) setVisor(true) }}
        disabled={!abrible || cargando}
        title={abrible ? a.filename : 'El archivo todavía se está procesando'}
        className={`flex items-center gap-2.5 rounded-[10px] border border-surface-faint bg-white/70 px-2.5 py-2 text-left transition-colors ${
          abrible ? 'hover:bg-surface' : 'cursor-default opacity-70'
        }`}
      >
        <span className={`shrink-0 ${TONO[a.kind]}`}>
          <IconoArchivo kind={a.kind} />
        </span>
        <span className="min-w-0">
          <span className="block max-w-[210px] truncate text-chat font-semibold text-ink">
            {a.filename}
          </span>
          <span className="block text-pill text-ink-muted">
            {cargando ? 'Cargando…'
              : !abrible ? `${ETIQUETA[a.kind]} · procesando`
              : `${ETIQUETA[a.kind]} · ver`}
          </span>
        </span>
      </button>
      {fallo && seMuestraAdentro && (
        <span className="text-pill font-semibold text-danger">
          No se pudo cargar. Toca la tarjeta para reintentar.
        </span>
      )}
      {visor && a.doc_id && (
        <VisorDeDocumento documentId={a.doc_id} filename={a.filename}
                          onClose={() => setVisor(false)} />
      )}
    </div>
  )
}
